/**
 * Which practice an agency admin is viewing — held per browser tab.
 *
 * The API client turns this into the `X-Practice-Id` header, while the selected location lives in
 * each tab's React state. Both must belong to the same tab: when the practice sat in localStorage
 * (shared by every tab), opening practice A in one tab silently switched another tab showing
 * practice B over to A, and its requests then paired practice A with a location of B — rejected
 * by the backend's tenancy guard, and no retry could fix it.
 *
 * So sessionStorage (one per tab) is the source of truth. localStorage keeps only the last practice
 * opened, so a brand-new tab can start where you left off; once a tab has its own value, nothing
 * another tab does changes it.
 */
export interface ImpersonatedPractice {
  id: string;
  name: string;
}

const STORAGE_KEY = 'pn_impersonation';

// In-memory copy for this tab, so the tab stays on its practice even where sessionStorage is
// unavailable. `undefined` = not read yet.
let current: ImpersonatedPractice | null | undefined;

function parse(raw: string | null): ImpersonatedPractice | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ImpersonatedPractice> | null;
    return value && typeof value.id === 'string' && typeof value.name === 'string'
      ? { id: value.id, name: value.name }
      : null;
  } catch {
    return null;
  }
}

function writeSession(practice: ImpersonatedPractice | null): void {
  try {
    // An explicit "null" records that this tab is not viewing a practice, so it is not re-seeded
    // from another tab's last choice on the next read.
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(practice));
  } catch {
    /* in-memory copy still holds it */
  }
}

/** The practice this tab is viewing, or null. */
export function readImpersonation(): ImpersonatedPractice | null {
  if (current !== undefined) return current;
  let sessionRaw: string | null = null;
  try {
    sessionRaw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    /* fall through to the seed */
  }
  if (sessionRaw !== null) {
    current = parse(sessionRaw);
    return current;
  }
  // First read in a new tab: start from the last practice opened in this browser.
  let seed: ImpersonatedPractice | null = null;
  try {
    seed = parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    /* no seed */
  }
  current = seed;
  writeSession(seed);
  return current;
}

export function writeImpersonation(practice: ImpersonatedPractice): void {
  current = practice;
  writeSession(practice);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(practice));
  } catch {
    /* only affects what a new tab starts on */
  }
}

export function clearImpersonation(): void {
  current = null;
  writeSession(null);
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* only affects what a new tab starts on */
  }
}
